import { supabaseBrowser } from '../supabaseBrowser';
import { competitionsCache, categoryCountsCache, CACHE_TTL } from '../cache';
import { questionForPlayers } from '../entry/question';

const supabase = supabaseBrowser;

// Cache user profile existence to avoid repeated checks
const userProfileCache = new Map();

export const competitionService = {
  async getAll(bypassCache = false) {
    // Check cache first (unless bypassed)
    if (!bypassCache) {
      const cached = competitionsCache.get('active_competitions');
      if (cached) {
        return cached;
      }
    }

    try {
      // CRITICAL FIX: Limit images and add better error handling to prevent JSON parsing errors
      // Fetch only essential fields for listing, avoid complex joins
      const { data, error } = await supabase
        ?.from('competitions')
        ?.select(
          `
          id,
          slug,
          title,
          description,
          short_description,
          prize_value,
          ticket_price,
          total_tickets,
          tickets_sold,
          draw_date,
          status,
          instant_win_type,
          is_featured,
          cover_image_url,
          created_at,
          competition_images (
            url,
            alt_text,
            display_order
          )
        `
        )
        ?.eq('status', 'active')
        ?.order('is_featured', { ascending: false })
        ?.order('created_at', { ascending: false })
        ?.not('competition_images.url', 'like', 'data:%')
        ?.limit(20);

      if (error) {
        console.error('Error fetching competitions:', error);
        throw error;
      }

      const competitionsWithImages = (data || [])?.map((row) => {
        const actualTicketsSold = row?.tickets_sold || 0;
        const createdAt = new Date(row?.created_at)?.getTime();
        const now = Date.now();
        const hoursActive = Math.max((now - createdAt) / (1000 * 60 * 60), 1);
        const salesVelocity = Math.round(actualTicketsSold / hoursActive);
        const images = (row?.competition_images || [])
          ?.sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0))
          ?.slice(0, 3);
        const primaryImage = images?.[0];

        return {
          id: row?.id,
          slug: row?.slug,
          title: row?.title || '',
          description: row?.description || '',
          shortDescription: row?.short_description || '',
          prizeValue: row?.prize_value,
          ticketPrice: row?.ticket_price,
          totalTickets: row?.total_tickets,
          ticketsSold: actualTicketsSold,
          drawDate: row?.draw_date,
          endDate: row?.draw_date,
          maxTickets: row?.total_tickets,
          status: row?.status,
          instant_win_type: row?.instant_win_type,
          salesVelocity: salesVelocity,
          createdAt: row?.created_at,
          isFeatured: row?.is_featured || false,
          image: primaryImage?.url || '/assets/images/no_image.png',
          coverImage: row?.cover_image_url || null,
          alt: primaryImage?.alt_text || `${row?.title} competition prize image`,
          images: (images || [])?.map((img) => ({
            url: img?.url,
            alt: img?.alt_text,
            displayOrder: img?.display_order,
          })),
          selectedNumbers: [],
        };
      });

      // Cache the result
      competitionsCache.set(
        'active_competitions',
        competitionsWithImages,
        CACHE_TTL.COMPETITIONS_LIST
      );

      return competitionsWithImages;
    } catch (error) {
      console.error('Competition service error:', error);
      return [];
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('competitions')
        ?.select(
          `
          id,
          slug,
          title,
          description,
          prize_value,
          ticket_price,
          total_tickets,
          tickets_sold,
          draw_date,
          status,
          instant_win_type,
          views,
          favorites,
          created_at,
          general_knowledge_question,
          winning_ticket,
          competition_images(url, alt_text, display_order)
        `
        )
        ?.eq('id', id)
        ?.single();

      if (error) {
        console.error('Error fetching competition by ID:', error);
        throw error;
      }

      // Images now contain storage URLs directly from database
      const primaryImage = data?.competition_images?.sort(
        (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
      )?.[0];

      // CRITICAL FIX: Calculate tickets_sold dynamically from selected_numbers count
      const actualTicketsSold = data?.tickets_sold || 0;

      // Calculate sales velocity based on lifetime sales
      const now = Date.now();
      const createdAt = new Date(data?.created_at)?.getTime();
      const hoursActive = Math.max((now - createdAt) / (1000 * 60 * 60), 1);
      const salesVelocity = Math.round(actualTicketsSold / hoursActive);

      return {
        id: data?.id,
        slug: data?.slug,
        title: data?.title,
        description: data?.description,
        prizeValue: data?.prize_value,
        ticketPrice: data?.ticket_price,
        totalTickets: data?.total_tickets,
        ticketsSold: actualTicketsSold,
        drawDate: data?.draw_date,
        endDate: data?.draw_date,
        maxTickets: data?.total_tickets,
        winningTicket: data?.winning_ticket,
        status: data?.status,
        instant_win_type: data?.instant_win_type,
        views: data?.views,
        favorites: data?.favorites,
        salesVelocity: salesVelocity,
        createdAt: data?.created_at,
        // Question and options only: the answer is checked on the server.
        generalKnowledgeQuestion: questionForPlayers(data?.general_knowledge_question),
        image: primaryImage?.url || '/assets/images/no_image.png', // Storage URL or fallback
        alt: primaryImage?.alt_text || `${data?.title} competition prize image`,
        images:
          data?.competition_images?.map((img) => ({
            url: img?.url, // Direct storage URL
            alt: img?.alt_text,
            displayOrder: img?.display_order,
          })) || [],
        selectedNumbers: [],
        ticketPurchases: [],
      };
    } catch (error) {
      console.error('Competition service error:', error);
      return null;
    }
  },

  async getBySlug(slug) {
    try {
      const { data, error } = await supabase
        ?.from('competitions')
        ?.select(
          `
          id,
          slug,
          title,
          description,
          prize_value,
          ticket_price,
          total_tickets,
          tickets_sold,
          draw_date,
          status,
          instant_win_type,
          views,
          favorites,
          created_at,
          general_knowledge_question,
          winning_ticket,
          competition_images(url, alt_text, display_order)
        `
        )
        ?.eq('slug', slug)
        ?.maybeSingle();

      // CRITICAL FIX: Handle PGRST116 error (no rows returned) gracefully
      // maybeSingle() returns null if no rows found instead of throwing error
      if (error) {
        console.error('Error fetching competition by slug:', error);
        throw error;
      }

      // If no competition found, return null instead of error
      if (!data) {
        console.warn(`Competition not found for slug: ${slug}`);
        return null;
      }

      // Images now contain storage URLs directly from database
      const primaryImage = data?.competition_images?.sort(
        (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
      )?.[0];

      // CRITICAL FIX: Calculate tickets_sold dynamically from selected_numbers count
      const actualTicketsSold = data?.tickets_sold || 0;

      // Calculate sales velocity based on lifetime sales
      const now = Date.now();
      const createdAt = new Date(data?.created_at)?.getTime();
      const hoursActive = Math.max((now - createdAt) / (1000 * 60 * 60), 1);
      const salesVelocity = Math.round(actualTicketsSold / hoursActive);

      return {
        id: data?.id,
        slug: data?.slug,
        title: data?.title,
        description: data?.description,
        prizeValue: data?.prize_value,
        ticketPrice: data?.ticket_price,
        totalTickets: data?.total_tickets,
        ticketsSold: actualTicketsSold,
        drawDate: data?.draw_date,
        endDate: data?.draw_date,
        maxTickets: data?.total_tickets,
        winningTicket: data?.winning_ticket,
        status: data?.status,
        instant_win_type: data?.instant_win_type,
        views: data?.views,
        favorites: data?.favorites,
        salesVelocity: salesVelocity,
        createdAt: data?.created_at,
        // Question and options only: the answer is checked on the server.
        generalKnowledgeQuestion: questionForPlayers(data?.general_knowledge_question),
        image: primaryImage?.url || '/assets/images/no_image.png', // Storage URL or fallback
        alt: primaryImage?.alt_text || `${data?.title} competition prize image`,
        images:
          data?.competition_images?.map((img) => ({
            url: img?.url, // Direct storage URL
            alt: img?.alt_text,
            displayOrder: img?.display_order,
          })) || [],
        selectedNumbers: [],
        ticketPurchases: [],
      };
    } catch (error) {
      console.error('Competition service error:', error);
      return null;
    }
  },

  async getAllCompetitions({
    category,
    status = 'active',
    sortBy = 'draw_date',
    sortOrder = 'asc',
  } = {}) {
    try {
      let query = supabase
        ?.from('competitions')
        ?.select(
          `
          id,
          slug,
          title,
          description,
          prize_value,
          ticket_price,
          total_tickets,
          tickets_sold,
          draw_date,
          status,
          category,
          created_at,
          winning_ticket,
          competition_images (
            id,
            url,
            alt_text,
            display_order
          )
        `
        )
        ?.eq('status', status);

      // Apply category filter if provided
      if (category) {
        query = query?.eq('category', category);
      }

      // Apply sorting
      const sortColumn =
        {
          draw_date: 'draw_date',
          ticket_price: 'ticket_price',
          prize_value: 'prize_value',
          popularity: 'tickets_sold',
        }?.[sortBy] || 'draw_date';

      query = query?.order(sortColumn, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) throw error;

      // CRITICAL FIX: Calculate ticketsSold dynamically from selected_numbers count
      // This ensures accurate real-time data directly from the database
      return (data || [])?.map((competition) => {
        // Calculate actual tickets sold from selected_numbers relationship
        const actualTicketsSold = competition?.tickets_sold || 0;
        const totalTickets = competition?.total_tickets || 1;
        const progress = Math.round((actualTicketsSold / totalTickets) * 100);
        const ticketsRemaining = totalTickets - actualTicketsSold;

        // Sort images by display_order and convert to proper format
        const sortedImages = (competition?.competition_images || [])
          ?.sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0))
          ?.map((img) => ({
            url: img?.url,
            alt: img?.alt_text,
            displayOrder: img?.display_order,
          }));

        return {
          id: competition?.id,
          slug: competition?.slug,
          title: competition?.title,
          description: competition?.description,
          prizeValue: competition?.prize_value,
          ticketPrice: competition?.ticket_price,
          totalTickets: totalTickets,
          ticketsSold: actualTicketsSold, // Use dynamic count from database
          ticketsRemaining: ticketsRemaining,
          progress: progress,
          drawDate: competition?.draw_date,
          status: competition?.status,
          category: competition?.category,
          winningTicket: competition?.winning_ticket,
          images: sortedImages || [],
          createdAt: competition?.created_at,
        };
      });
    } catch (error) {
      console.error('Error fetching competitions:', error);
      throw error;
    }
  },

  async getCompetitionCountByCategory(bypassCache = false) {
    // Check cache first
    if (!bypassCache) {
      const cached = categoryCountsCache.get('category_counts');
      if (cached) {
        return cached;
      }
    }

    try {
      const { data, error } = await supabase
        ?.from('competitions')
        ?.select('category')
        ?.eq('status', 'active');

      if (error) throw error;

      // Count competitions per category
      const counts = {
        cash: 0,
        holidays: 0,
        products: 0,
        experiences: 0,
        all: data?.length || 0,
      };

      (data || [])?.forEach((comp) => {
        if (comp?.category && counts?.[comp?.category] !== undefined) {
          counts[comp.category]++;
        }
      });

      // Cache the result
      categoryCountsCache.set('category_counts', counts, CACHE_TTL.CATEGORY_COUNTS);

      return counts;
    } catch (error) {
      console.error('Error fetching category counts:', error);
      return {
        cash: 0,
        holidays: 0,
        products: 0,
        experiences: 0,
        all: 0,
      };
    }
  },

  // The number grid, for guests and players alike: every sold number and
  // every number other players are holding (no player ids), plus the
  // signed-in player's own purchases and holds. One database call.
  async getNumberGrid(competitionId) {
    const { data, error } = await supabase?.rpc('get_competition_number_grid', {
      p_competition_id: competitionId,
    });

    if (error) throw error;

    const grid = data || {};
    return {
      total: grid?.total || 0,
      serverTime: grid?.server_time || null,
      sold: Array.isArray(grid?.sold) ? grid.sold : [],
      mySold: Array.isArray(grid?.my_sold) ? grid.my_sold : [],
      held: (grid?.held || []).map((h) => ({ number: h?.n, expiresAt: h?.e })),
      mine: (grid?.mine || []).map((h) => ({
        number: h?.n,
        expiresAt: h?.e,
        reservationId: h?.id,
        type: h?.type,
      })),
    };
  },

  // Every sold number in the competition (yours and other players').
  async getSelectedNumbers(competitionId) {
    const grid = await this.getNumberGrid(competitionId);
    return [...grid.sold, ...grid.mySold].sort((a, b) => a - b);
  },

  // Numbers other players are holding: [{ number, expiresAt }].
  async getReservedNumbers(competitionId) {
    const grid = await this.getNumberGrid(competitionId);
    return grid.held;
  },

  async getUserReservedNumbers(competitionId, userId) {
    // Get selection reservations (not cart reservations)
    const { data, error } = await supabase?.rpc('get_user_selection_reservations', {
      comp_id: competitionId,
      user_uuid: userId,
    });

    if (error) throw error;

    return (
      data?.map((row) => ({
        number: row?.number_reserved,
        expiresAt: row?.expires_at,
        reservationId: row?.reservation_id,
      })) || []
    );
  },

  async reserveMultipleNumbers(competitionId, userId, numbers) {
    try {
      // PERFORMANCE FIX: Only check profile once, use cache
      await this.ensureUserProfileCached(userId);

      // Use new selection reservation function instead of cart reservation
      const { data, error } = await supabase?.rpc('reserve_numbers_for_selection', {
        comp_id: competitionId,
        user_uuid: userId,
        ticket_numbers: numbers,
      });

      if (error) throw error;

      // Process results and return success/error information
      const results = {
        successful: [],
        failed: [],
        errors: [],
      };

      data?.forEach((result) => {
        if (result?.status === 'selected') {
          results?.successful?.push({
            number: result?.number_reserved,
            reservationId: result?.reservation_id,
            // The real hold time from the database (10 minutes for picks).
            expiresAt: result?.expires_at,
          });
        } else {
          results?.failed?.push(result?.number_reserved);
          results?.errors?.push({
            number: result?.number_reserved,
            message: result?.error_message,
          });
        }
      });

      return results;
    } catch (error) {
      // Provide user-friendly error messages
      if (error?.message?.includes('foreign key constraint')) {
        throw new Error(
          'Unable to reserve tickets. Please try refreshing the page and logging in again.'
        );
      }
      throw error;
    }
  },

  async releaseMultipleReservations(competitionId, userId, numbers) {
    try {
      const { data, error } = await supabase?.rpc('release_multiple_reservations', {
        comp_id: competitionId,
        user_uuid: userId,
        ticket_numbers: numbers,
      });

      if (error) throw error;

      return data; // Returns count of released reservations
    } catch (error) {
      throw error;
    }
  },

  async getCompetitionNumberStatus(competitionId) {
    try {
      const { data, error } = await supabase?.rpc('get_competition_number_status', {
        comp_id: competitionId,
      });

      if (error) throw error;

      // Convert to more convenient format
      const numberStatus = {};
      data?.forEach((row) => {
        numberStatus[row?.number_value] = {
          status: row?.status,
          reservedBy: row?.reserved_by,
          expiresAt: row?.expires_at,
          selectedBy: row?.selected_by,
          selectedAt: row?.selected_at,
        };
      });

      return numberStatus;
    } catch (error) {
      throw error;
    }
  },

  // Expired holds are cleared by the database (every minute, and whenever
  // someone takes the number), so there is no client-side clean-up.

  async reserveNumber(competitionId, userId, number) {
    try {
      // PERFORMANCE FIX: Only check profile once, use cache
      await this.ensureUserProfileCached(userId);

      // PERFORMANCE FIX: Remove conflict resolution - migration already handles this
      // Migration has proper indexes and constraints to prevent conflicts

      const { data, error } = await supabase?.rpc('reserve_ticket_number', {
        comp_id: competitionId,
        user_uuid: userId,
        ticket_number: number,
      });

      if (error) throw error;

      return data; // Returns reservation ID
    } catch (error) {
      // Enhanced error handling for better user experience
      if (error?.message?.includes('foreign key constraint')) {
        throw new Error(
          'Unable to reserve ticket. Please try refreshing the page and logging in again.'
        );
      } else if (error?.message?.includes('already purchased')) {
        throw new Error('This number has already been purchased by another user.');
      } else if (error?.message?.includes('currently reserved')) {
        throw new Error(
          'This number is temporarily reserved by another user. Please try again in a few minutes.'
        );
      } else if (error?.message?.includes('just reserved by another user')) {
        throw new Error(
          'This number was just taken by another user. Please select a different number.'
        );
      }
      throw error;
    }
  },

  // PERFORMANCE FIX: New cached version to avoid repeated profile checks
  async ensureUserProfileCached(userId) {
    // Check cache first
    if (userProfileCache?.has(userId)) {
      return userProfileCache?.get(userId);
    }

    try {
      // Check if profile exists
      const { data: profile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('id', userId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        throw fetchError;
      }

      if (!profile) {
        // Profile doesn't exist, get user data and create it
        const {
          data: { user },
        } = await supabase?.auth?.getUser();
        if (!user || user?.id !== userId) {
          throw new Error('Authentication required');
        }

        const profileData = {
          id: userId,
          email: user?.email,
          full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User',
          phone: user?.user_metadata?.phone || null,
          // No role here: a player's own metadata must never set it.
          avatar_url: user?.user_metadata?.avatar_url || null,
          date_of_birth: user?.user_metadata?.date_of_birth || null,
        };

        // Use UPSERT to handle potential race conditions with trigger
        const { error: insertError } = await supabase?.from('user_profiles')?.upsert(profileData, {
          onConflict: 'id',
          ignoreDuplicates: false,
        });

        if (insertError) {
          console.warn('Profile creation warning (may be normal):', insertError);
        }
      }

      // Cache the result (profile exists now)
      userProfileCache?.set(userId, true);
      return true;
    } catch (error) {
      console.error('Failed to ensure user profile:', error);
      // Don't throw here - let the main operation continue
      return false;
    }
  },

  // Keep original for backward compatibility
  async ensureUserProfile(userId) {
    return this.ensureUserProfileCached(userId);
  },

  async releaseReservation(competitionId, userId, number) {
    // Releases the player's own hold on this number (pick or basket). The
    // database announces the release to every viewer's grid.
    await this.releaseMultipleReservations(competitionId, userId, [number]);
    return true;
  },

  async selectNumbers(competitionId, userId, numbers, purchaseId) {
    try {
      // First, reserve all numbers
      const reservations = [];
      for (const number of numbers) {
        const reservationId = await this.reserveNumber(competitionId, userId, number);
        reservations?.push({ number, reservationId });
      }

      // For immediate purchase flow, complete the reservations
      if (purchaseId && purchaseId !== 'temp_' + Date.now()) {
        for (const { reservationId } of reservations) {
          await supabase?.rpc('complete_reservation', {
            reservation_uuid: reservationId,
            purchase_uuid: purchaseId,
          });
        }
      }

      return reservations;
    } catch (error) {
      throw error;
    }
  },

  async completeReservation(reservationId, purchaseId) {
    const { data, error } = await supabase?.rpc('complete_reservation', {
      reservation_uuid: reservationId,
      purchase_uuid: purchaseId,
    });

    if (error) throw error;

    return data;
  },

  // Releases a number the player is holding. Bought tickets are never
  // removed from here.
  async removeNumber(competitionId, userId, number) {
    return this.releaseReservation(competitionId, userId, number);
  },

  async getUserSelectedNumbers(competitionId, userId) {
    const { data, error } = await supabase
      ?.from('selected_numbers')
      ?.select('number_selected')
      ?.eq('competition_id', competitionId)
      ?.eq('user_id', userId)
      ?.order('number_selected');

    if (error) throw error;

    return data?.map((row) => row?.number_selected) || [];
  },

  // Everything the number grid needs, in one call (guests included). The
  // signed-in player is taken from their session, not from `userId`.
  async getNumberStates(competitionId) {
    const grid = await this.getNumberGrid(competitionId);
    return {
      selected: [...grid.sold, ...grid.mySold].sort((a, b) => a - b), // every sold number
      othersPurchased: grid.sold,
      userPurchased: grid.mySold,
      reserved: grid.held, // numbers other players are holding
      userReserved: grid.mine, // your picks and basket tickets in this competition
      serverTime: grid.serverTime, // the database's clock, for hold end times
    };
  },

  // New method to move selections to cart when "Add to Cart" is clicked
  // Picked numbers go into the basket through the server, which checks the
  // answer to the competition question first. `userId` is unused (the server
  // uses the session) and kept for older callers.
  async moveSelectionsToCart(competitionId, userId, numbers, answer) {
    const res = await fetch('/api/entry/add-to-basket', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ competitionId, numbers, answer }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok && !(data?.moved?.length > 0)) {
      const err = new Error(data?.error || 'Couldn’t add your tickets. Please try again.');
      err.wrongAnswer = Boolean(data?.wrongAnswer);
      throw err;
    }

    return {
      successful: (data?.moved || []).map((number) => ({ number })),
      failed: data?.failed || [],
      errors: (data?.failed || []).map((number) => ({ number, message: data?.error })),
    };
  },

  // New method to release selection reservations when user deselects
  async releaseSelectionReservations(competitionId, userId, numbers) {
    try {
      const { data, error } = await supabase?.rpc('release_selection_reservations', {
        comp_id: competitionId,
        user_uuid: userId,
        ticket_numbers: numbers,
      });

      if (error) throw error;

      return data; // Returns count of released reservations
    } catch (error) {
      throw error;
    }
  },

  // Instant wins are awarded by the database when a paid entry is recorded,
  // so there is nothing for the browser to call after a purchase.

  /**
   * Get instant win prizes for a competition (public list: amount and
   * won/available; the ticket number only once it has been won).
   * @param {string} competitionId - Competition ID
   * @returns {Promise<Array>} Array of instant win prizes with claimed status
   */
  async getInstantWinPrizes(competitionId) {
    try {
      const { data, error } = await supabase?.rpc('get_instant_win_prizes_public', {
        p_competition_id: competitionId,
      });

      if (error) {
        console.error('Error fetching instant win prizes:', error);
        throw error;
      }

      return (
        data?.map((prize) => ({
          id: prize?.id,
          ticketNumber: prize?.ticket_number ?? null,
          prizeAmount: prize?.prize_amount,
          isClaimed: !!prize?.is_claimed,
          claimedAt: prize?.claimed_at ?? null,
          winnerName: prize?.winner_name ?? null,
          winnerId: prize?.winner_id ?? null,
        })) || []
      );
    } catch (error) {
      console.error('Error in getInstantWinPrizes:', error);
      throw error;
    }
  },

  // Live grid updates: see subscribeToGrid() in lib/realtime/liveCompetitions.js.
};
